import type { Matter } from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import {
  buildFirstPartyUmUimCoverageRow,
  buildPolicyLimitConfirmationCoverageRow,
  buildTcrCoverageRow,
  buildThirdPartyCoverageRow,
  getClientFirstPartyAutoInsurances,
  getInvestigationCoverageStatusBadgeClass,
  getPrimaryThirdPartyInsurance,
  formatLiabilityStatusLabel,
  getInvestigationThirdPartyInsurances,
  type InvestigationCoverageBadge,
  type InvestigationCoverageRow,
  type InvestigationCoverageSubitem,
} from "../matter-overview-utils";

function renderInvestigationCoverageValues(
  item: Pick<
    InvestigationCoverageRow | InvestigationCoverageSubitem,
    "status" | "statusTone" | "note" | "badges"
  >,
  keyPrefix: string,
) {
  if (item.badges?.length) {
    return item.badges.map((badge: InvestigationCoverageBadge, index) => (
      <span
        key={`${keyPrefix}-${badge.label}-${index}`}
        className={getInvestigationCoverageStatusBadgeClass(badge.tone)}
      >
        {badge.label}
      </span>
    ));
  }

  if (!item.status || !item.statusTone) {
    return null;
  }

  return (
    <>
      <span
        className={getInvestigationCoverageStatusBadgeClass(item.statusTone)}
      >
        {item.status}
      </span>
      {item.note ? (
        <span className="text-muted-foreground text-xs">{item.note}</span>
      ) : null}
    </>
  );
}

function hasInvestigationCoverageValues(
  item: Pick<
    InvestigationCoverageRow | InvestigationCoverageSubitem,
    "status" | "statusTone" | "badges"
  >,
) {
  return Boolean(item.badges?.length || (item.status && item.statusTone));
}

export function LiabilityCoverage({
  matter,
  title = "Liability & Coverage",
  embedded = false,
}: {
  matter: Matter;
  title?: string;
  embedded?: boolean;
}) {
  const thirdPartyInsurances = getInvestigationThirdPartyInsurances(matter);
  const clientAutoInsurances = getClientFirstPartyAutoInsurances(matter);
  if (embedded) {
    for (const insurance of matter.insurances) {
      if (insurance.coverageEnabled === false) continue;
      const target =
        insurance.type === "third_party_liability"
          ? thirdPartyInsurances
          : insurance.type === "first_party"
            ? clientAutoInsurances
            : null;
      if (target && !target.some((item) => item.id === insurance.id))
        target.push(insurance);
    }
  }
  const primaryThirdPartyInsurance = getPrimaryThirdPartyInsurance(matter);
  const thirdPartyInsurance = embedded
    ? (thirdPartyInsurances.find(
        (insurance) => insurance.id === primaryThirdPartyInsurance?.id,
      ) ?? thirdPartyInsurances.at(0))
    : primaryThirdPartyInsurance;
  const liabilityDecision = thirdPartyInsurance?.liability
    ?.trim()
    .toLowerCase();
  const coverageRows: InvestigationCoverageRow[] = [
    ...(matter.incidentReportNotApplicable
      ? []
      : [buildTcrCoverageRow(matter)]),
    buildPolicyLimitConfirmationCoverageRow(matter),
    buildThirdPartyCoverageRow(matter, thirdPartyInsurances),
    ...(clientAutoInsurances.length > 0
      ? [
          buildFirstPartyUmUimCoverageRow(
            matter,
            clientAutoInsurances,
            "First Party (UIM) Coverage",
            "uim",
          ),
          buildFirstPartyUmUimCoverageRow(
            matter,
            clientAutoInsurances,
            "First Party (UM) Coverage",
            "um",
          ),
        ]
      : []),
    {
      key: "liability-acceptance",
      label: "Liability acceptance",
      status: formatLiabilityStatusLabel(thirdPartyInsurance),
      statusTone:
        liabilityDecision === "accepted"
          ? "green"
          : liabilityDecision === "denied"
            ? "red"
            : liabilityDecision === "pending"
              ? "amber"
              : "gray",
      note: liabilityDecision ? undefined : "Missing",
    },
  ];
  return (
    <div
      className={cn(
        "bg-card min-w-0 rounded-2xl border p-6",
        embedded && "rounded-none border-0 p-0",
      )}
    >
      <h3
        className={cn(
          "text-foreground mb-6 text-sm font-semibold break-words",
          embedded && "mb-1 border-b pb-4",
        )}
      >
        {title}
      </h3>
      <div className="space-y-0">
        {coverageRows.map((row) => (
          <div key={row.key} className="border-b py-4 last:border-b-0">
            <div
              className={cn(
                "flex min-w-0 flex-wrap items-center justify-between gap-4",
                row.subitems && row.subitems.length > 0 && "mb-2",
              )}
            >
              <p className="text-foreground min-w-0 text-sm break-words">
                {row.label}
              </p>
              {!row.subitems && hasInvestigationCoverageValues(row) && (
                <div className="flex flex-wrap items-center gap-2">
                  {renderInvestigationCoverageValues(row, row.key)}
                </div>
              )}
            </div>
            {row.subitems?.map((subitem) => (
              <div
                key={subitem.id}
                className="flex min-w-0 flex-wrap items-center justify-between gap-4 py-2 pl-4"
              >
                <p className="text-muted-foreground min-w-0 text-xs break-words">
                  {subitem.label}
                </p>
                {hasInvestigationCoverageValues(subitem) ? (
                  <div className="flex flex-wrap items-center gap-2">
                    {renderInvestigationCoverageValues(subitem, subitem.id)}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
